import { useEffect, useRef, useState } from 'react';

import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { Stepper } from '@/components/Stepper';
import { JOURNEY_STEP } from '@/components/stepper.steps';
import type { TranslationKey } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';
import { captureCandidateSession, SCREEN, useKioskSession } from '@/lib/kioskSession';
import type { HandoffControl } from '@/services/handoff.contract';
import { HANDOFF_CONTROL } from '@/services/handoff.contract';
import { registerPayCard } from '@/services/payCard';
import { fetchProxyIdPrefix } from '@/services/payCardPrefix';
import { useBack } from '@/shared/hooks/useBack';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { usePayCardErrand } from '../hooks/usePayCardErrand';
import { isEnterableRead, isPayCardProxyIdShape, withoutSeparators } from '../proxyId';
import { PAY_CARD_STATE } from '../types';
import { ProxyIdField } from './ProxyIdField';
import { ScanOverlay } from './ScanOverlay';

/**
 * Why this screen refused the value. Three failures, one message area — see the fields' `errorId`.
 *
 * `badFormat` is the local half of decision 0181's rule and it arrived on 2026-09-10 with TASK-005's app
 * half: the api half had shipped alone, so a candidate typing 15 digits got a bare 400 on an unattended
 * screen. `rejected` is still what the api says, including a 16-digit id whose prefix is wrong — the half
 * this app does not check.
 */
type PayCardError = 'mismatch' | 'badFormat' | 'rejected';

/**
 * The copy for each, as a TOTAL record rather than a chain of ternaries.
 *
 * It was `error === 'mismatch' ? … : t('payCard.rejected')`, which silently labelled any third state as a
 * rejection — the api's word for *we refused this*, which is the one thing a local format failure is not.
 * A fourth state now has to bring its own copy or fail the typecheck.
 */
const ERROR_COPY: Record<PayCardError, TranslationKey> = {
  mismatch: 'payCard.mismatch',
  badFormat: 'payCard.badFormat',
  rejected: 'payCard.rejected',
};

/**
 * The message both fields point at with `aria-describedby`.
 *
 * A module constant, not a literal repeated three times in the tree: the two fields and the paragraph have
 * to agree, and an id that disagrees is silent — the message simply stops reaching a screen reader with
 * nothing on screen to show it.
 */
const PAY_CARD_ERROR_ID = 'pay-card-error';

/**
 * Which form the two by-hand fields take [03-us-kpc-1e; decision 0423].
 *
 * - `pending` — the prefix read has not answered. The fields take the fallback entry meanwhile.
 * - `masked` — the read answered: both fields show the prefix and take the digits after it.
 * - `plain` — the read did not answer, or the candidate typed before it did. The fallback entry [AC-7]: 16
 *   digits, digits only, grouped in fours, nothing fixed, and no error.
 *
 * **One form for both fields, and it is decided once.** There is one prefix, and the two entries are
 * compared as the plain 16 digits whatever form produced them. Leaving `pending` is final: *"a field never
 * switches form once it holds a digit"*, so a read that lands after the candidate has started typing is
 * ignored rather than rewriting what they see.
 */
type EntryForm =
  | { readonly kind: 'pending' }
  | { readonly kind: 'masked'; readonly prefix: string }
  | { readonly kind: 'plain' };

const PLAIN: EntryForm = { kind: 'plain' };

/**
 * Pay-card registration, before the onboarding hand-off
 * [KPC-1; Figma · Tail · Pay card (double-entry), `12021:41960` and `12021:42047`].
 *
 * **Everybody takes a pay card** [decision 0089, 2026-08-18: *"It is a rule"*], which RESOLVES OQ-66 — the
 * question this story's body still guards against. So there is no "skip, I use direct deposit" control
 * anywhere on this screen. It is mandatory and informational, and it does NOT gate: KAP-2 asserts that no
 * tail step blocks the candidate [decision 0046], and the Operator ruled that mandatory means required, not
 * blocking.
 *
 * **The proxy id never enters the session.** It lives in this component's state for the length of one
 * request and the session holds only the OUTCOME. It identifies a financial instrument, and the convention
 * skill forbids bank details on the device.
 *
 * **One screen** — camera, an `OR TYPE IT IN` divider, then both fields [decision 0135; Figma
 * `11 · Get set up (pay card)` 12275:27124 desktop · 12275:27170 iPad · 12275:27215 mobile].
 *
 * It was two steps until 2026-09-01, and that split was an Operator ruling of 2026-08-24 rather than drift.
 * The frames it named have since been deleted and the redraw puts both routes back on one surface, so 0135
 * records the reversal — the only trace of the original split was a `_note` in the fidelity harness.
 */
export function PayCardScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  /**
   * **Which of the two screens this is** — and therefore where every exit from it leads.
   *
   * Figma draws this twice. `12 · Scan` sits in the new-applicant flow and carries the journey rail;
   * `R2 · Pay card — scan` is the one a returning associate reaches from *What brings you in today?* and
   * has no rail at all. The two are otherwise identical, down to the copy.
   *
   * ⚠️ It used to be a selector written out here, and the one screen that shared the distinction wrote its
   * own copy of it. It now lives in {@link usePayCardErrand} with the reasoning and the defect history —
   * the rail, Back and the destination all read the same signal, and three copies of a condition is how
   * two of them come to disagree.
   */
  const arrivedForAPayCard = usePayCardErrand();
  /*
   * Back is NOT the same exit as `whenDone`, and conflating them broke a test that exists for the
   * difference. Backing out of the MAIN flow returns to the confirmation — this screen sits after a
   * submitted application, and ending here would discard somebody who has already applied [0135] — where
   * finishing the flow continues forward to `finish`. Two directions, two destinations.
   *
   * On the menu route both happen to be the menu: there is no confirmation to go back to, because that
   * candidate never applied for anything.
   *
   * ⚠️ The journey destination is now `payCardWhy` rather than `submitted`, because that is the screen
   * immediately before this one on that route [`11 · Why a pay card`]. Back skipping a screen backwards
   * is how a candidate who went one step too far loses the instruction that told them where the packets
   * are — which is the whole reason that screen exists.
   */
  const back = useBack(arrivedForAPayCard ? SCREEN.arrivalReason : SCREEN.payCardWhy);
  const candidateId = useKioskSession((state) => state.lookupResult?.candidateId);
  const setPayCard = useKioskSession((state) => state.setPayCard);
  const goTo = useKioskSession((state) => state.goTo);
  const summonRepresentative = useKioskSession((state) => state.summonRepresentative);

  const [scanning, setScanning] = useState(false);
  /*
   * Where focus lands when the camera layer closes with a value in hand — see `fillFromScan`. The field is
   * mounted the whole time [0135: one screen, both routes on it], so this is a plain focus target and not a
   * mount-time effect.
   */
  const scannedField = useRef<HTMLInputElement | null>(null);
  /*
   * The last scan decoded something that is not a proxy id [KPC-1c; Figma `New user / 10 · Scan — failed
   * read (state)`]. Its own state rather than a fourth `PayCardError`: the frame reports it under the CAMERA,
   * as a callout, while those three are about the two fields and are announced against them.
   */
  const [failedRead, setFailedRead] = useState(false);
  const camera = useRef<HTMLButtonElement | null>(null);
  /*
   * What each field HOLDS: the digits after the prefix — all 16 in the fallback, whose prefix is empty.
   * Never the separators — they are display only [0423].
   */
  const [first, setFirst] = useState('');
  const [second, setSecond] = useState('');
  const [error, setError] = useState<PayCardError | null>(null);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState<EntryForm>({ kind: 'pending' });
  const prefix = form.kind === 'masked' ? form.prefix : '';

  /*
   * The prefix, from the api [0423 point 5]. A read that lands after the form was decided — the candidate
   * typed first — changes nothing; a read that does not answer is the plain form, with no error on screen.
   */
  useEffect(() => {
    let mounted = true;
    void fetchProxyIdPrefix().then((read) => {
      if (!mounted) return;
      setForm((current) =>
        current.kind !== 'pending'
          ? current
          : read === null
            ? PLAIN
            : { kind: 'masked', prefix: read },
      );
    });
    return () => {
      mounted = false;
    };
  }, []);

  /** Typing while the read is still out settles the form as plain: a field never switches once it holds a digit. */
  function enter(set: (value: string) => void): (value: string) => void {
    /*
     * Decided by the form this handler was RENDERED under — what the candidate saw — not by the queued
     * state. A read that lands a moment before the keystroke has already queued `masked`, and the keystroke's
     * update is queued after it: judging `current` would keep `masked` under a digit typed into a plain field.
     */
    const typedIntoPlain = form.kind !== 'masked';
    return (value) => {
      if (value !== '') setForm((current) => (typedIntoPlain ? PLAIN : current));
      // Typing is the candidate taking the other route; the failed-read report is about the scan.
      setFailedRead(false);
      set(value);
    };
  }

  /**
   * The entry as it is compared and sent: **the plain 16 digits** [0423; 03-us-kpc-1e] — the prefix (empty
   * in the fallback) and what was typed after it.
   */
  function entered(held: string): string {
    return prefix + held;
  }

  /*
   * Still "both boxes have something in them", NOT "both are valid" — the Save control must stay reachable
   * with a value this screen knows it will refuse.
   *
   * Disabling it on a bad format would be the same dead end in a politer costume: a candidate who typed 15
   * digits would press a button that does nothing, on an unattended screen, with nobody to ask why. The
   * message below is the point — they have to be able to ASK for it.
   *
   * What a field HOLDS, not what it shows: the prefix alone is not an entry [03-us-kpc-1e].
   */
  const bothEntered = first.length > 0 && second.length > 0;

  /**
   * Puts a confirmed barcode read into the form — **both fields** — and leaves the candidate on the screen.
   *
   * **The scan FILLS the form; it does not submit it** [decision 0142 § *Decision — the flow, as Heidi
   * specified it*, step 4: *"that would then maybe populate the second proxy ID card field"*, and step 5's
   * *"please confirm"* before the closeout; Operator, 2026-09-11]. Until then the accepted read went
   * straight into `registerPayCard` and **never touched `first` or `second`**, so every outcome that kept
   * the candidate here — a read that is not 16 digits, an id Bullhorn refuses — printed its reason under
   * two EMPTY boxes. Being told *the pay card ID is 16 digits* with nothing on screen to count is the
   * defect the Operator reported on 2026-09-11, and the scanned digits never being visible is why it read
   * as a form that had not been filled in at all.
   *
   * **One scan fills both boxes, and that does not weaken the double entry.** 0037's rule catches a
   * MISTYPE — *"a mistype means you wouldn't get paid"* — and a camera does not mistype; the candidate has
   * already checked the digits on the layer's confirm step [0187, KPC-1 § *The read is offered for
   * CONFIRMATION*], which is the scan path's equivalent of typing it again. **The typed path is untouched**:
   * paste and drag are still suppressed on the confirmation field, and two entries that disagree are still
   * refused — including when one of them was scanned and then edited here.
   *
   * There is no second code path to Bullhorn any more either. Whatever the camera read leaves this screen
   * through `save()` below, so the format rule, the mismatch rule, the rejection copy and the recruiter
   * fallback are the ones the by-hand path already proves.
   */
  function fillFromScan(proxyId: string): void {
    // Spaces and hyphens are ignored, because separators are display only [03-us-kpc-1e AC-6].
    const scanned = withoutSeparators(proxyId);
    setScanning(false);
    /*
     * **A confirmed read is entered ONLY when it is exactly 16 digits that begin with the prefix**
     * [03-us-kpc-1e; decisions 0187, 0423]. A decoded barcode is a string from a camera pointed at whatever
     * the candidate was holding — the packet's 12-digit UPC-A is the likeliest — and it has no more claim
     * to be a proxy id than something typed.
     *
     * ⚠️ **The BACKSTOP, since 2026-10-16.** `ScanOverlay` now refuses such a read before it is offered
     * [KPC-1c] and {@link reportFailedRead} reports it, so a confirmed read reaching here already passed the
     * same check against the same prefix. This stays because the invariant names the confirm control as an
     * entry point in its own right, and KPC-1e AC-6's answer — not entered, the fields keep what they held,
     * the format message shows — is the right one if it ever is reached. **Never completed with the prefix**
     * either — a short read is a misread, not a suffix.
     */
    if (!isEnterableRead(scanned, prefix)) {
      setError('badFormat');
      scannedField.current?.focus();
      return;
    }
    // A scan is an entry like a keystroke: it settles a still-pending form as plain.
    const fill = scanned.slice(prefix.length);
    enter(setFirst)(fill);
    setSecond(fill);
    setError(null);
    /*
     * Focus follows the value (convention skill § 9 — *when a screen changes, focus moves to it*). The
     * layer that held focus has just gone, and leaving it on `document.body` would make a keyboard or
     * screen-reader candidate tab from the top of the page to find out whether anything happened. Landing
     * on the first field announces the label, the digits that arrived, and — when the read was refused —
     * `aria-invalid` and the sentence saying why.
     */
    scannedField.current?.focus();
  }

  /**
   * The camera decoded something that is not a proxy id: the layer closes WITHOUT offering it, the fields
   * keep what they held, and the screen says the card was not read [KPC-1c: *"reported as a failed read
   * rather than offered for confirmation"*; eval-kpc-1c step 4; Figma `New user / 10 · Scan — failed read
   * (state)`]. Not a dead end [0187; BR-K7]: the camera is right there to try again, the fields are on the
   * same screen, and *Request assistance* is in the footer — the three routes the frame's own copy names.
   */
  function reportFailedRead(): void {
    setScanning(false);
    setFailedRead(true);
  }

  /*
   * Focus goes to the control the report's first instruction names — *try the scan again* — once the layer
   * has gone. Not inside `reportFailedRead`: that runs from the layer's own effect, before the layer has
   * finished focusing its heading, and the heading then unmounts with focus on it, leaving `document.body`.
   */
  useEffect(() => {
    if (failedRead) camera.current?.focus();
  }, [failedRead]);

  async function save(): Promise<void> {
    /*
     * Compared BEFORE anything is sent. The whole point of the double entry is that a mistyped digit is
     * caught here rather than on payday, and sending the first value while the second disagreed would make
     * the second field decoration.
     */
    const proxyId = entered(first);
    if (proxyId !== entered(second)) {
      setError('mismatch');
      return;
    }
    /*
     * **16 digits, checked here so the candidate is not refused by a bare 400** [decision 0181, whose
     * *"16 characters"* wording the Operator corrected to digits on 2026-09-10; `contracts.md` § B]. The
     * api is still the authority and still refuses a bad id — `pay-card.dto.ts` and `ProxyIdFormatPipe`
     * are unchanged, and a client that accepted what they reject is the drift § B names. This exists
     * because a candidate who types 15 digits at an unattended kiosk got a 400 with nothing on screen
     * explaining what to fix.
     *
     * **Only the length-and-digits half.** In the masked form the prefix is fixed, so a typed value cannot
     * carry a wrong one and what is left to get wrong is the digits after it — an entry short of them gets
     * this message [0423; 03-us-kpc-1e]. In the plain fallback a 16-digit id with the wrong prefix goes to
     * the api and comes back `rejected`; the api keeps the whole rule either way.
     *
     * AFTER the mismatch check on purpose: when the two entries disagree we do not know which one the
     * candidate meant, and *"type it again"* is the instruction that fits. When they agree, the value is
     * their intent and the length is the thing to tell them about.
     */
    if (!isPayCardProxyIdShape(proxyId)) {
      setError('badFormat');
      return;
    }
    if (candidateId === undefined) {
      // No identified candidate means nothing to attach the card to. A recruiter keys it instead.
      handOffToRecruiter(undefined);
      return;
    }

    setError(null);
    setSending(true);
    const stillTheirs = captureCandidateSession();
    const outcome = await registerPayCard(candidateId, proxyId);
    setSending(false);
    /*
     * ⛔ **A registration that lands after Exit is dropped, whatever it says** [KID-1, KID-2: one visit, one
     * person] — the same rule as the profile page. Applied after Exit and YES, it recorded this person's
     * pay card in the next person's session and moved them to *Pay card linked*, or, on an `unreachable`
     * answer, QUEUED A REPRESENTATIVE in their name: `summonRepresentative` is once per session, so that
     * also spent the one call the next person may need. A `registered` card stays registered in Bullhorn
     * either way. An `unreachable` one leaves their card unlinked, as Exit leaves anything they had not
     * finished [OQ-101]; a row raised now would carry the next person's candidate id, or none, not theirs.
     */
    if (!stillTheirs()) return;

    if (outcome.kind === 'registered') {
      setPayCard(PAY_CARD_STATE.registered);
      /*
       * ⚠️ **It used to exit the flow here**, straight to `finish` or back to the arrival menu, so the
       * candidate did the one thing that decides whether they get paid and was told nothing about it.
       * `13 · Pay card linked` / `R3 · Pay card linked` are what the design draws in that gap at six
       * viewports, and the prototype reaches the same one screen from every successful read. The two
       * destinations are not lost — {@link PayCardLinkedScreen}'s Continue is where each of them moved.
       */
      goTo(SCREEN.payCardLinked);
      return;
    }
    if (outcome.kind === 'rejected') {
      setError('rejected');
      return;
    }
    /*
     * Unreachable — including when the environment has writes disabled. A failed write must NEVER read as a
     * success: a candidate told they are registered when they are not finds out on payday, which is the
     * worst possible place. The recruiter takes it from here.
     */
    handOffToRecruiter(undefined);
  }

  /**
   * Hands this candidate to a person — and now actually PUTS ONE ON THE QUEUE.
   *
   * ⚠️ **It promised a recruiter and queued nobody**, which is the worst form of the defect decision 0206
   * exists to end: *never promise a person without queueing one; never queue one without being asked.*
   * Recorded in `GAP-REGISTER.md` (2026-09-11, *"same defect still live in three more places"*), fixed
   * here because this change moves the screen this function belongs to.
   *
   * All four callers qualify under BOTH limbs of the rule, which is why the raise sits in the one function
   * rather than on one button:
   *
   * - *Request assistance* in the footer — an explicit ask, and the most obviously broken of the four.
   * - *Ask a representative* on the camera layer — an explicit ask.
   * - a save with no identified candidate — the recruiter keys the id instead, and nothing else will.
   * - a write that did not reach Bullhorn — the candidate is moved on without a registered card, and a
   *   pay card that silently never registered is discovered on payday.
   *
   * The last two matter more than they look, because the screen they exit to does not cover them. `finish`
   * raises an entry only on its NO-submission branch, so a candidate on the application journey — who by
   * definition has one — was handed to a recruiter who was never told. `setPayCard` below has recorded
   * `handedToRecruiter` since the screen was written and nothing has ever read it.
   *
   * `summonRepresentative` is idempotent for the whole candidate session, so asking twice, or asking after
   * the I-9 route already queued its own labelled row, still leaves exactly one entry.
   *
   * ⭐ **`control` is named by the CALLER, and only *Request assistance* names one** [KOP-1g scenario 1]: its
   * entry reads *pay card in person*. ⛔ The other three name none and keep *ready for recruiter* — no record
   * gives them a reason [KOP-1g § Open] — which is why the control is a parameter here and not a constant.
   * REQUIRED, so a bare `onClick={handOffToRecruiter}` does not compile: `Button` passes the click event, and an
   * event as the control would throw inside `JSON.stringify` and lose the person silently [KOP-1g review r1].
   */
  function handOffToRecruiter(control: HandoffControl | undefined): void {
    summonRepresentative(undefined, control);
    setPayCard(PAY_CARD_STATE.handedToRecruiter);
    goTo(SCREEN.finish);
  }
  const requestAssistance = (): void =>
    handOffToRecruiter(HANDOFF_CONTROL.payCardRequestAssistance);

  return (
    <KioskScreen>
      <KioskCard
        band={arrivedForAPayCard ? undefined : <Stepper current={JOURNEY_STEP.getSetUp} />}

        footer={
          <div className="flex w-full flex-col gap-4 sm:flex-row sm:items-center sm:justify-end">
            {/*
              The frame's own pair. "Request assistance" is the hand-off — it is NOT a skip: it puts the
              candidate on the recruiter queue and continues, which is what 0089 permits and what BR-K7
              requires of a screen nobody can complete alone.
            */}
            <Button onClick={requestAssistance} variant="secondary">
              {t('payCard.needHelp')}
            </Button>
            <Button
              disabled={!bothEntered || sending}
              onClick={() => {
                void save();
              }}
            >
              {t('payCard.save')}
            </Button>
          </div>
        }
        /*
          The status chip is drawn on this screen as on every other [12275:27124 @ 1038,257] and was
          simply absent. The declared departure on this frame covers its RHYTHM and slot order, not
          whether the chip exists — and it says this screen follows Jobs and Finish, both of which draw it.
        */
        header={
          <>
            <BackLink onClick={back} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
      >
        <div className="flex w-full flex-col items-start gap-6 py-8">
          <div className="flex w-full flex-col items-start gap-2">
            <h1
              className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-[var(--foreground)]"
              ref={heading}
              tabIndex={-1}
            >
              {t('payCard.heading')}
            </h1>
            <p className="w-full text-sm leading-5 text-[var(--muted-foreground)]">
              {t('payCard.intro')}
            </p>
            {/*
              What the camera does, read BEFORE the candidate points it [KPC-1c's order; KPC-1d
              `payCard.securityFraming`; decision 0490's words]. No frame draws it, so it takes the intro's
              own style and sits under it, above the camera control; nothing else on the screen moves.
            */}
            <p className="w-full text-sm leading-5 text-[var(--muted-foreground)]">
              {t('payCard.securityFraming')}
            </p>
          </div>

          {/*
            ONE screen: the camera, a divider, then both fields — `11 · Get set up (pay card)`
            (12275:27124 desktop · 12275:27170 iPad · 12275:27215 mobile) [decision 0135].

            It used to be two steps, and that was not drift: the Operator split it on 2026-08-24 and the
            frames that ruling named have since been DELETED. The redraw puts the two routes back on one
            surface, and 0135 records the reversal — without it the next reader finds a build that
            contradicts Figma and no trace of why.

            What the split was protecting is kept: a candidate whose card will not read never has to fail a
            scan first, because the fields are right there.
          */}
          {/*
            ⚠️ **A control that OPENS the camera, where the frame draws the camera itself.** The frame's dark
            box is a placeholder — a `Camera viewport` frame whose only child reads "Kiosk / device camera" —
            for a live feed embedded in the page.

            The build keeps the existing `ScanOverlay` behind it, and the reason is not effort: that overlay
            carries the permission prompt, the denied / unsupported / unavailable states and the "ask for a
            person" exit, all of which a kiosk on a branch network actually hits. Inlining the feed would
            have to reproduce every one of those on a screen that also holds the manual fields. Declared in
            `design-qa/known-departures.md` so the fidelity check reports it once rather than every run.
          */}
          <button
            /*
              The failed-read frame rings the camera box in the error colour. The callout below says why in
              words, so the state is not carried by colour alone (convention skill § 8).
            */
            className={`flex h-40 w-full items-center justify-center rounded-lg bg-[#0f1c2e] text-sm font-medium text-white ${failedRead ? 'border-2 border-[var(--error)]' : ''}`}
            onClick={() => {
              setFailedRead(false);
              setScanning(true);
            }}
            ref={camera}
            type="button"
          >
            {t('payCard.scanTitle')}
          </button>

          {failedRead ? (
            /*
              `New user / 10 · Scan — failed read (state)`: an error callout directly under the camera, two
              lines, before "OR TYPE IT IN". Announced, because the layer that was speaking has just closed.
            */
            <div className="w-full" role="alert">
              <Callout tone="error">
                <span className="block">{t('payCard.failedRead.title')}</span>
                <span className="mt-[10px] block">{t('payCard.failedRead.body')}</span>
              </Callout>
            </div>
          ) : null}

          {/*
            The frame puts this between the camera and the manual fields [12441:27925], and it is the only
            line on the screen that says WHERE the barcode is — the question a candidate holding an
            unfamiliar card actually has. It is centred under the box it describes.
          */}
          <p className="w-full text-center text-sm leading-5 text-[var(--muted-foreground)]">
            {t('payCard.scanHint')}
          </p>

          <p className="w-full text-center text-xs uppercase leading-4 tracking-[1.44px] text-[var(--faint-foreground)]">
            {t('payCard.orTypeItIn')}
          </p>

          {/*
            Both fields point at the ONE message below, because every failure this screen reports is about
            the pair: the two entries disagree, the value is not 16 digits, or the api refused it. Marking
            one box would be a guess about which one the candidate got wrong.
          */}
          <ProxyIdField
            errorId={error === null ? undefined : PAY_CARD_ERROR_ID}
            id="pay-card-id"
            label={t('payCard.idLabel')}
            onChange={enter(setFirst)}
            // A paste or drop the mask refuses is not entered, and the format message says why [AC-6].
            onRefused={() => {
              setError('badFormat');
            }}
            prefix={prefix}
            ref={scannedField}
            value={first}
          />
          <div className="flex w-full flex-col items-start gap-1">
            <ProxyIdField
              errorId={error === null ? undefined : PAY_CARD_ERROR_ID}
              id="pay-card-id-confirm"
              label={t('payCard.idConfirmLabel')}
              onChange={enter(setSecond)}
              prefix={prefix}
              suppressPaste
              value={second}
            />
            {/*
              The hint the frame puts under the second field, and it earns its place: it says WHY paste is
              off and what a typo costs — "a mistype means you wouldn't get paid". The old build stated the
              rule in a warning callout above the fields without ever giving the reason.
            */}
            <p className="w-full text-xs leading-4 text-[var(--faint-foreground)]">
              {t('payCard.confirmHint')}
            </p>
          </div>

          {error === null ? null : (
            /*
              Announced, not just coloured: a red border says nothing to a screen reader or to anyone who
              cannot distinguish it (convention skill § 8).
              
              The colour is `--error`, not the warning tone it carried. Both of these are FAILURES the
              candidate has to act on — the two entries disagree, or the card was refused — and the warning
              tone is the one this product uses for «here is why something behaves unusually», which is a
              different thing. Every other field on the kiosk already reports a bad value in `--error`, so
              this one was also the odd one out.
            */
            <p
              className="w-full text-sm leading-5 text-[var(--error)]"
              id={PAY_CARD_ERROR_ID}
              role="alert"
            >
              {t(ERROR_COPY[error])}
            </p>
          )}
        </div>
      </KioskCard>

      {scanning ? (
        <ScanOverlay
          onAccept={fillFromScan}
          onFailedRead={reportFailedRead}
          prefix={prefix}
          onAskRecruiter={() => {
            setScanning(false);
            handOffToRecruiter(undefined);
          }}
          onTypeInstead={() => {
            setScanning(false);
          }}
        />
      ) : null}
    </KioskScreen>
  );
}
